import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import EditableTableHeader from 'sharedComponents/EditableTableHeader'
import { selectRootUser } from '@/rootSlice'
import {
  fetchTrafficReport,
  saveTrafficConfig,
  selectTabPagination,
  tabNameToReportKey,
} from '../trafficReportSlice'
import { trafficSessionsConfig } from 'modules/formatting'
import { reportDefaultInvisibleColumns } from 'modules/reportConfigs'
import { resolveVisibleSortedKeys, parseDefaultInvisibleSet } from '../columnVisibility'

interface Props {
  tabName: string
  visibleKeys: string[]
  names: Record<string, string>
  // Refetch context — passed through so sort changes can refresh the page
  timePeriod: string
  customDateRange?: { start: string; end: string }
  filters?: any[]
}

/**
 * Inline editable table header for Traffic data tabs. Mirrors the Dashboard's
 * column-config UX: drag handles to reorder, X buttons to remove a column,
 * click on column titles to toggle sort. All changes persist to
 * user_preferences.traffic_config and refetch the active tab on sort change.
 */
export default function TrafficEditableThead({
  tabName,
  visibleKeys,
  names,
  timePeriod,
  customDateRange,
  filters,
}: Props) {
  const dispatch = useDispatch()
  const user: any = useSelector(selectRootUser)
  const tabPagination = useSelector((state) => (selectTabPagination as any)(state, tabName))
  const reportKey = tabNameToReportKey(tabName)

  if (!reportKey) return null

  const trafficconfig = user?.trafficconfig || { columns: {} }
  const columnConfigs: Record<string, any> = trafficconfig?.columns?.[reportKey] || {}

  // Effective sort: prefer the user's saved selection, otherwise fall back to
  // the backend's default of `last_date desc` so the caret indicator stays
  // truthful for users who haven't picked a sort yet.
  let sortBy: string = 'last_date'
  let sortAsc: boolean = false
  for (const k of Object.keys(columnConfigs)) {
    const dir = columnConfigs[k]?.sort
    if (dir === 'asc' || dir === 'desc') {
      sortBy = k
      sortAsc = dir === 'asc'
      break
    }
  }

  // Persist a partial config (deep-merge under columns[reportKey]).
  const applyAndSave = (partial: any, opts: { refetch?: boolean } = {}) => {
    const nextColumns = _.merge({}, trafficconfig.columns || {}, partial.columns || {})
    const tabCols: Record<string, any> = nextColumns[reportKey] || {}

    // If the active sort column was just hidden, clear its sort and pick the
    // first visible sortable column as the new default sort.
    let needsRefetch = !!opts.refetch
    const allKeys = trafficSessionsConfig.keys as string[]
    const sortableSet = new Set(trafficSessionsConfig.sortableFields as string[])
    const defaultInvisibleSet = parseDefaultInvisibleSet((reportDefaultInvisibleColumns as any)[reportKey])
    const visibleAfter = resolveVisibleSortedKeys(allKeys, tabCols, defaultInvisibleSet)

    for (const k of Object.keys(tabCols)) {
      const dir = tabCols[k]?.sort
      if ((dir === 'asc' || dir === 'desc') && tabCols[k]?.invisible) {
        tabCols[k] = { ...tabCols[k], sort: null }
        const fallback = visibleAfter.find((vk) => sortableSet.has(vk))
        if (fallback) {
          tabCols[fallback] = { ...tabCols[fallback], sort: 'desc' }
        }
        needsRefetch = true
      }
    }
    nextColumns[reportKey] = tabCols

    const nextConfig = { ...trafficconfig, ...partial, columns: nextColumns }
    ;(dispatch as any)(saveTrafficConfig(nextConfig)).then(() => {
      if (needsRefetch) {
        ;(dispatch as any)(
          fetchTrafficReport({
            timePeriod,
            customDateRange,
            tabName,
            filters,
            pagination: tabPagination || { pageIndex: 1, pageSize: 20 },
          } as any)
        )
      }
    })
  }

  const onColumnsChange = (partial: any) => applyAndSave(partial)

  const onSort = (key: string, asc: boolean) => {
    // Switching to a new column defaults to descending; clicking the active
    // column toggles between desc and asc.
    const effectiveAsc = key === sortBy ? asc : false
    const partialCols: Record<string, any> = {}
    for (const k of Object.keys(columnConfigs)) {
      if (k !== key && columnConfigs[k]?.sort) {
        partialCols[k] = { ...columnConfigs[k], sort: null }
      }
    }
    partialCols[key] = { ...columnConfigs[key], sort: effectiveAsc ? 'asc' : 'desc' }
    applyAndSave({ columns: { [reportKey]: partialCols } }, { refetch: true })
  }

  return (
    <EditableTableHeader
      keys={visibleKeys}
      names={names}
      allKeys={trafficSessionsConfig.keys as string[]}
      columnConfigs={columnConfigs}
      configKey={reportKey}
      onColumnsChange={onColumnsChange}
      sortableFields={trafficSessionsConfig.sortableFields as string[]}
      sortBy={sortBy}
      sortAsc={sortAsc}
      onSort={onSort}
    />
  )
}
